import type { RuleEvent } from './ruleEvent.ts';
import type { ObservedDraw } from './dice.ts';
import type { TurnResult } from './turn.ts';
import type { RaceResult } from './race.ts';

// Cache decoded receipts, but retain the actual throws in the race as well.
// Reloads, saves and development refreshes must not turn Roll into a no-op.
type Reading = Pick<RuleEvent, 'description' | 'ruleCitation'> & { actorIds?: readonly string[] };
export interface SavedDiceReceipt {
  version: 1;
  draws: readonly ObservedDraw[];
  /**
   * [draw index, description, citation, actor ids?]. The fourth element was
   * added 7 Oct 2026 so a reloaded race can still put the right driver cards on
   * the dice table; receipts saved before then carry three and show no cards.
   */
  readings: ([number, string, string] | [number, string, string, string[]])[];
}
const turns = new WeakMap<TurnResult, readonly ObservedDraw[]>();
const finishes = new WeakMap<RaceResult, readonly ObservedDraw[]>();
const resolvedAt = new WeakMap<object, number>();
const readings = new WeakMap<ObservedDraw, Reading[]>();

/** Called when a rule has returned, never while its outcome still needs dice. */
export function markResolved(events: readonly RuleEvent[], drawCount: number) {
  for (const event of events) if (!resolvedAt.has(event)) resolvedAt.set(event, drawCount);
}
export function eventResolvedAt(event: object) { return resolvedAt.get(event); }
export function readingsForDraw(draw: ObservedDraw): readonly Reading[] { return readings.get(draw) ?? []; }
/**
 * A DRAFT is decided by one die — the drafter's own red die — but the draft
 * phase records all its drafts at once, so by resolution count every one of
 * them would land on the phase's LAST die: one wall of text on one roll
 * (7 Oct 2026). Each draft goes on the die that decided it instead: the next
 * unclaimed red die thrown FOR its drafter, showing the face the event records.
 * Never later than its resolution; anything that does not match falls back.
 */
function draftDie(event: RuleEvent, draws: readonly ObservedDraw[], count: number, claimed: Set<ObservedDraw>): ObservedDraw | undefined {
  if (event.eventType !== 'draft' || event.dice?.length !== 1) return undefined;
  const face = event.dice[0].dice[0];
  const drafter = event.actorIds[0];
  return draws.find(d => !claimed.has(d) && d.index <= count - 1 && d.role === 'red' && d.subject?.[0] === drafter && d.face === face);
}
function bindReadings(events: readonly RuleEvent[], draws: readonly ObservedDraw[]) {
  const claimed = new Set<ObservedDraw>();
  for (const event of events) {
    // A quality sequence contains the actual individual rulings. Its summary
    // would repeat all of them on the last throw, so reveal the children only.
    const challenges = (event as RuleEvent & { challenges?: RuleEvent[] }).challenges;
    if (challenges?.length && challenges.every(child => resolvedAt.has(child))) { bindReadings(challenges, draws); continue; }
    const count = resolvedAt.get(event);
    if (count === undefined) continue;
    const own = draftDie(event, draws, count, claimed);
    if (own) claimed.add(own);
    const draw = own ?? draws.find(d => d.index >= count - 1);
    if (!draw) continue;
    const list = readings.get(draw) ?? [];
    list.push(event);
    readings.set(draw, list);
  }
}
export function retainTurnDraws(turn: TurnResult, draws: readonly ObservedDraw[]) {
  turns.set(turn, draws);
  bindReadings(turn.events, draws);
  turn.diceReceipt = saveReceipt(draws);
}
export function retainFinishDraws(race: RaceResult, draws: readonly ObservedDraw[]) {
  finishes.set(race, draws);
  bindReadings(race.photoFinish?.attempts ?? [], draws);
  race.finishDiceReceipt = saveReceipt(draws);
}
function saveReceipt(draws: readonly ObservedDraw[]): SavedDiceReceipt {
  return { version: 1, draws, readings: draws.flatMap((draw, index) =>
    readingsForDraw(draw).map(event => event.actorIds?.length
      ? [index, event.description, event.ruleCitation, [...event.actorIds]] as [number, string, string, string[]]
      : [index, event.description, event.ruleCitation] as [number, string, string])) };
}
function restoreReceipt(receipt: SavedDiceReceipt): readonly ObservedDraw[] {
  for (const [index, description, ruleCitation, actorIds] of receipt.readings) {
    const draw = receipt.draws[index];
    if (!draw) continue;
    const list = readings.get(draw) ?? [];
    list.push(Array.isArray(actorIds) ? { description, ruleCitation, actorIds } : { description, ruleCitation });
    readings.set(draw, list);
  }
  return receipt.draws;
}
export function drawsForTurn(turn: TurnResult): readonly ObservedDraw[] | undefined {
  if (!turns.has(turn) && turn.diceReceipt?.version === 1) turns.set(turn, restoreReceipt(turn.diceReceipt));
  return turns.get(turn);
}
export function drawsAtFinish(race: RaceResult): readonly ObservedDraw[] {
  if (!finishes.has(race) && race.finishDiceReceipt?.version === 1) finishes.set(race, restoreReceipt(race.finishDiceReceipt));
  return finishes.get(race) ?? [];
}
